'use client'

import type { FormAction } from '@/core/action'
import { ReloadIcon } from '@radix-ui/react-icons'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useActionState, useEffect, useMemo } from 'react'
import { SubmitButton } from '../components/submit-button'
import { useToast } from '../components/use-toast'
import { AutoForm, type AutoFormSchema, Form } from '../form'

type SignInFormProps = {
  action: FormAction<boolean>
}

export const SignInForm = ({ action }: SignInFormProps) => {
  const t = useTranslations('pages.signIn')
  const { toast } = useToast()

  const [state, formAction] = useActionState(action, null)

  useEffect(() => {
    if (state?.success) {
      toast({
        variant: 'default',
        title: t('form.toasts.success.title'),
        description: t('form.toasts.success.description'),
      })
    }

    if (state?.errors.length) {
      const errorMessage = state.errors[0]?.message

      if (errorMessage === 'account_inaccessible') {
        toast({
          variant: 'destructive',
          title: t('form.toasts.accountInaccessible.title'),
          description: t('form.toasts.accountInaccessible.description'),
        })
      } else {
        toast({
          variant: 'destructive',
          title: t('form.toasts.invalidLogin.title'),
          description: t('form.toasts.invalidLogin.description'),
        })
      }
    }
  }, [state, toast, t])

  const schema: AutoFormSchema = useMemo(
    () => [
      {
        path: 'email',
        component: 'email',
        classNames: { root: 'mb-4' },
        options: {
          label: t('form.fields.email.label'),
          placeholder: t('form.fields.email.placeholder'),
        },
      },
      {
        path: 'password',
        component: 'password',
        classNames: { root: 'mb-4' },
        options: {
          label: t('form.fields.password.label'),
          placeholder: t('form.fields.password.placeholder'),
        },
      },
    ],
    [t],
  )

  if (state?.success) {
    return (
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">{t('success.title')}</h1>

        <p className="px-8 text-center text-normal text-muted-foreground pb-6">
          {t('success.description')}
        </p>
        <ReloadIcon className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <Form state={state} action={formAction}>
      <AutoForm schema={schema} />
      <SubmitButton
        className="w-full mt-6"
        loadingText={t('form.actions.loading')}
        actionText={t('form.actions.login')}
      />
      <p className="pt-4 px-8 text-center text-sm text-muted-foreground">
        {t('actions.resetPassword.title')}
        <br />
        <Link
          href="/auth/reset-password"
          className="underline underline-offset-4 hover:text-primary"
        >
          {t('actions.resetPassword.action')}
        </Link>
      </p>
    </Form>
  )
}
